import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import LaboratoryPicker from 'components/LaboratoryPicker';
import useAuth from 'hooks/useAuth';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Two pieces on purpose: the button lives inside the user menu (a Popover),
 * but the dialog must be rendered OUTSIDE it - closing the menu unmounts
 * everything inside the Popover, dialog included.
 */

/** Compact entry in the user menu: shows the current laboratory. */
export const LaboratorySwitchButton: FC<{ onClick: () => void }> = ({
  onClick,
}) => {
  const { t } = useTranslation();
  const { user, laboratories } = useAuth();

  if (laboratories.length < 2) return null;

  const current = laboratories.find(({ id }) => id === user?.laboratoryId);

  return (
    <Box sx={{ p: 1 }}>
      <Button
        fullWidth
        color="primary"
        onClick={onClick}
        sx={(theme) => ({
          justifyContent: 'space-between',
          textAlign: 'left',
          textTransform: 'none',
          px: 1.5,
          py: 1,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
        })}
        endIcon={<SwapHorizRoundedIcon />}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary' }}
          >
            {t('Current laboratory')}
          </Typography>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ maxWidth: 190, color: 'text.primary' }}
          >
            {current?.companyName ?? '-'}
          </Typography>
        </Box>
      </Button>
    </Box>
  );
};

interface LaboratorySwitchDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Searchable dialog to switch laboratory, however many the user has. */
export const LaboratorySwitchDialog: FC<LaboratorySwitchDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user, laboratories, selectLaboratory } = useAuth();
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const handleClose = () => {
    if (!selectingId) onClose();
  };

  const handleSelect = async (laboratoryId: string) => {
    setSelectingId(laboratoryId);
    try {
      await selectLaboratory(laboratoryId);
      onClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('Could not select the laboratory'), {
        variant: 'error',
      });
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('Switch laboratory')}</DialogTitle>
      <DialogContent>
        <LaboratoryPicker
          laboratories={laboratories}
          currentId={user?.laboratoryId}
          selectingId={selectingId}
          onSelect={handleSelect}
          columns={1}
          maxListHeight="60vh"
        />
      </DialogContent>
    </Dialog>
  );
};
